import {
  daysBetween,
  describeDaysLeft,
  formatIsoDate,
  formatQuantity,
  type IsoDate,
  itemEmoji,
  STORAGE_LABEL,
  type StoragePlace,
  storageLabel,
} from "@larder/shared";
import type { TrackedItem } from "../data/items";

/**
 * One line in a list.
 *
 * The days-left phrase carries the urgency, so it is the emphasised element
 * rather than the date — "tomorrow" prompts action in a way "2026-08-10" never
 * does. The exact date stays visible underneath for anyone who wants it.
 *
 * An EATEN item tells a different story, so it gets different words. Showing an
 * expiry date it never reached, next to a "—", reads as a row the app forgot to
 * finish; "Eaten · yesterday" reads as the win it actually was.
 */
export function ItemRow({
  item,
  places = [],
  today,
  onClick,
  index = 0,
  count = 1,
}: {
  item: TrackedItem;
  places?: StoragePlace[];
  /** The household's today. Needed only to date a resolved item. */
  today?: IsoDate;
  onClick?: () => void;
  index?: number;
  /** How many of this thing are in the house, if more than one. */
  count?: number;
}) {
  const { derived } = item;
  const eaten = derived.status === "consumed";
  const urgency = eaten
    ? "good"
    : derived.daysLeft === null
      ? "none"
      : derived.daysLeft < 0
        ? "past"
        : derived.daysLeft <= 2
          ? "soon"
          : "ok";

  const where = storageLabel(item.storageId, places, item.storage, STORAGE_LABEL);
  const when =
    eaten && derived.resolvedOn && today
      ? describeDaysLeft(daysBetween(today, derived.resolvedOn))
      : null;

  /**
   * The meta line, most useful fact first.
   *
   * How much there is beats where it lives: "1 L" answers "do I need to buy
   * milk?", which is the question the list exists for, while the place is only
   * useful once you have decided to go and get it. Absent until enrichment
   * lands, and the line simply closes up — no placeholder, no dash.
   */
  const amount = item.size ? formatQuantity(item.size) : null;
  const meta = when
    ? `${where} · eaten ${when}`
    : [
        amount,
        where,
        derived.effectiveExpiry
          ? formatIsoDate(derived.effectiveExpiry, item.expiryPrecision)
          : null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <button
      type="button"
      className="item-row"
      style={{ animationDelay: `${Math.min(index, 6) * 45}ms` }}
      onClick={onClick}
      disabled={!onClick}
      data-testid={`item-${item.name}`}
    >
      {/* Decorative. Every row shows its name right beside this, so a screen
          reader gains nothing from "package emoji" — and the fallback glyph is
          a guess about a category, which is the last thing worth announcing. */}
      <span className="item-glyph" aria-hidden="true">
        {itemEmoji(item.emoji, item.category)}
      </span>
      <span className="item-main">
        <span className="item-name">
          {item.name}
          {count > 1 && (
            <span className="dupe" data-testid={`count-${item.name}`}>
              ×{count}
            </span>
          )}
        </span>
        <span className="item-meta">{meta}</span>
      </span>
      <span className={`item-when ${urgency}`}>
        {eaten
          ? "Eaten"
          : derived.daysLeft === null
            ? "—"
            : describeDaysLeft(derived.daysLeft)}
      </span>
    </button>
  );
}
